// =============================================================================
// Form schema — matches the shape stored in form_templates.schema_json
// =============================================================================
// The database treats form_templates as configuration. Adding a new form type
// means inserting a row with a schema_json value of this shape. The runtime
// renderer in FormRunner.tsx reads it and renders the fields.
//
// Fields supported at launch: text, textarea, number, select, multi, yesno,
// yesno_na, date, datetime, site_picker, project_picker, employee_picker,
// swms_multi, photo, signature.
// Conditional visibility: show_if accepts {field, equals} or {field, in:[...]}
// =============================================================================

export type FieldKind =
  | "text"
  | "textarea"
  | "number"
  | "select"
  | "multi"
  | "yesno"
  | "yesno_na"
  | "date"
  | "datetime"
  | "time"
  | "site_picker"
  | "project_picker"
  | "employee_picker"
  | "swms_multi"
  | "photo"
  | "signature"
  | "signon_group";

export interface FieldDef {
  key: string;
  label: string;
  type: FieldKind;
  required?: boolean;
  placeholder?: string;
  help?: string;
  options?: string[] | { value: string; label: string }[];
  min?: number;
  max?: number;
  step?: number;
  rows?: number;
  min_photos?: number;
  max_photos?: number;
  show_if?: {
    field: string;
    equals?: unknown;
    in?: unknown[];
  };
}

export interface SectionDef {
  title: string;
  description?: string;
  fields: FieldDef[];
}

export interface FormSchema {
  sections: SectionDef[];
}

export type FormValues = Record<string, unknown>;

// -----------------------------------------------------------------------------
// Visibility evaluator
// -----------------------------------------------------------------------------
export function isVisible(field: FieldDef, values: FormValues): boolean {
  if (!field.show_if) return true;
  const v = values[field.show_if.field];
  if ("equals" in field.show_if) return v === field.show_if.equals;
  if ("in" in field.show_if && Array.isArray(field.show_if.in)) return field.show_if.in.includes(v);
  return true;
}

// -----------------------------------------------------------------------------
// Validation
// -----------------------------------------------------------------------------
export interface ValidationError { key: string; message: string; sectionIndex: number }

export function validateForm(schema: FormSchema, values: FormValues): ValidationError[] {
  const errors: ValidationError[] = [];
  schema.sections.forEach((section, si) => {
    for (const field of section.fields) {
      if (!isVisible(field, values)) continue;
      const v = values[field.key];
      if (field.required && isEmpty(v, field.type)) {
        errors.push({ key: field.key, sectionIndex: si, message: `${field.label} is required` });
        continue;
      }
      if (field.type === "number" && v != null && v !== "") {
        const n = Number(v);
        if (Number.isNaN(n)) errors.push({ key: field.key, sectionIndex: si, message: `${field.label} must be a number` });
        if (field.min != null && n < field.min) errors.push({ key: field.key, sectionIndex: si, message: `${field.label} must be ≥ ${field.min}` });
        if (field.max != null && n > field.max) errors.push({ key: field.key, sectionIndex: si, message: `${field.label} must be ≤ ${field.max}` });
      }
      if (field.type === "photo" && field.min_photos) {
        const arr = Array.isArray(v) ? v : [];
        if (arr.length < field.min_photos) errors.push({ key: field.key, sectionIndex: si, message: `At least ${field.min_photos} photo(s) required for ${field.label}` });
      }
    }
  });
  return errors;
}

function isEmpty(v: unknown, kind: FieldKind): boolean {
  if (v == null) return true;
  if (typeof v === "string") return v.trim().length === 0;
  if (Array.isArray(v)) return v.length === 0;
  if (kind === "yesno" || kind === "yesno_na") return v !== true && v !== false && v !== "na";
  return false;
}
